/**
 * Agnostic utility to remove sensitive information from any object or array.
 * Useful for logging, analytics, and debugging.
 */
export const sanitizePayload = (payload: unknown): unknown => {
  if (!payload) return null;

  if (Array.isArray(payload)) {
    return payload.map((item) => sanitizePayload(item));
  }

  if (typeof payload !== "object" || payload instanceof Date) return payload;

  const SENSITIVE_FIELDS = [
    "password",
    "token",
    "accessToken",
    "refreshToken",
    "apiKey",
    "api-key",
    "api_key",
    "secret",
    "creditCard",
    "cvv",
    "ssn",
    "authorization",
  ];

  const normalizeSpecialObjects = (value: unknown): unknown => {
    //Normalizes complex objects (like Axios headers, Fetch Headers, etc.) into plain objects
    if (!value || typeof value !== "object") return value;
    if (value instanceof Date) return value;

    const maybeToJson = (value as { toJSON?: unknown }).toJSON;
    if (typeof maybeToJson === "function") {
      try {
        return maybeToJson.call(value) as unknown;
      } catch {
        // ignore normalization errors
      }
    }

    const maybeEntries = (value as { entries?: unknown }).entries;
    if (typeof maybeEntries === "function") {
      try {
        const entriesIterable = maybeEntries.call(value) as Iterable<
          readonly [PropertyKey, unknown]
        >;
        return Object.fromEntries(entriesIterable);
      } catch {
        // ignore normalization errors
      }
    }

    const maybeForEach = (value as { forEach?: unknown }).forEach;
    if (typeof maybeForEach === "function") {
      try {
        const out: Record<string, unknown> = {};
        (maybeForEach as (cb: (value: unknown, key: unknown) => void) => void).call(
          value,
          (v: unknown, k: unknown) => {
            out[String(k)] = v;
          }
        );
        return out;
      } catch {
        // ignore normalization errors
      }
    }

    return value;
  };

  const sanitizeObject = (obj: Record<string, unknown>): Record<string, unknown> => {
    const sanitized: Record<string, unknown> = {};

    for (const key in obj) {
      if (Object.hasOwn(obj, key)) {
        const lowerKey = key.toLowerCase();
        const isSensitive = SENSITIVE_FIELDS.some((field) =>
          lowerKey.includes(field.toLowerCase())
        );

        if (isSensitive) {
          sanitized[key] = "[REDACTED]";
        } else if (typeof obj[key] === "object" && obj[key] !== null) {
          sanitized[key] = sanitizePayload(normalizeSpecialObjects(obj[key]));
        } else {
          sanitized[key] = obj[key];
        }
      }
    }
    return sanitized;
  };

  const normalized = normalizeSpecialObjects(payload);
  if (!normalized || typeof normalized !== "object" || normalized instanceof Date) {
    return normalized;
  }

  return sanitizeObject(normalized as Record<string, unknown>);
};
